// Article body for /blog/video-interview-questions. Plain HTML; metadata lives in ../meta/video-interview-questions.ts.
export default `
<p><strong>Video interview questions</strong> for a recorded (one-way or AI) interview have to work without an interviewer: there is no one to clarify them, no follow-up, and the candidate usually has one attempt within a time limit. Good ones are self-contained, answerable in 60–90 seconds, about one situation, and specific enough that two answers can be compared. Below are 25 video interview questions to ask candidates — warm-up, behavioral questions by role family and practical questions — with the evidence a strong answer contains, plus the rules for writing your own and a quality checklist.</p>

<h2>What makes a good video interview question?</h2>
<table>
<thead><tr><th>Rule</th><th>Why it matters in a recorded interview</th></tr></thead>
<tbody>
<tr><td>One situation, not a career history</td><td>The time limit is short; broad questions produce rambling answers</td></tr>
<tr><td>Build the follow-up into the question</td><td>There is no interviewer to ask "and what happened next?" — so ask it in the prompt</td></tr>
<tr><td>Ask for an action and an outcome</td><td>Gives you comparable evidence rather than opinions</td></tr>
<tr><td>Plain language, no jargon</td><td>Anything ambiguous will be answered eight different ways</td></tr>
<tr><td>Start easy</td><td>First-time nerves are real; a warm-up question settles candidates</td></tr>
<tr><td>Same questions for everyone on the role</td><td>Consistency is the point — and why a badly worded question hurts every answer equally</td></tr>
</tbody>
</table>
<figure><img src="/blog/figures/video-interview-questions-anatomy.png" alt="Anatomy of a good recorded video interview question: one situation, the candidate's own action, an outcome, answerable in 90 seconds" loading="lazy" decoding="async" width="1800" height="585"><figcaption>A good recorded question contains its own follow-up.</figcaption></figure>

<h2>25 video interview questions to ask candidates</h2>

<h3>Warm-up and motivation</h3>
<table>
<thead><tr><th>#</th><th>Question</th><th>A strong answer contains</th></tr></thead>
<tbody>
<tr><td>1</td><td>"In about a minute, tell us what attracted you to this role and what you hope to do more of in your next job."</td><td>A specific link to the role's content, not praise for the company</td></tr>
<tr><td>2</td><td>"Walk us through the part of your current or last job that is closest to this role."</td><td>Concrete tasks and the candidate's own part in them</td></tr>
<tr><td>3</td><td>"What would you want to learn in your first three months here?"</td><td>Realistic expectations that match the role</td></tr>
</tbody>
</table>

<h3>Customer service and support</h3>
<table>
<thead><tr><th>#</th><th>Question</th><th>A strong answer contains</th></tr></thead>
<tbody>
<tr><td>4</td><td>"Describe a time a customer was angry with you or your company. What were they upset about, what did you do, and how did it end?"</td><td>A real incident, de-escalation, ownership without blame, an outcome</td></tr>
<tr><td>5</td><td>"Tell us about a time you could not give a customer what they asked for. How did you explain it?"</td><td>Clear, honest communication and an alternative offered</td></tr>
<tr><td>6</td><td>"How do you keep quality up when the queue is long?"</td><td>A specific habit, not "I work hard"</td></tr>
</tbody>
</table>

<h3>Sales and account management</h3>
<table>
<thead><tr><th>#</th><th>Question</th><th>A strong answer contains</th></tr></thead>
<tbody>
<tr><td>7</td><td>"Tell us about a deal you lost or walked away from. Why, and what did you change afterwards?"</td><td>Qualification reasoning, honesty, a lesson applied</td></tr>
<tr><td>8</td><td>"Describe how you prepare for a first call with a new prospect."</td><td>A repeatable method and research sources</td></tr>
<tr><td>9</td><td>"Tell us about a client relationship you rescued. What was going wrong and what did you do?"</td><td>Diagnosis, action and a measurable result</td></tr>
</tbody>
</table>

<h3>Operations, logistics and shift work</h3>
<table>
<thead><tr><th>#</th><th>Question</th><th>A strong answer contains</th></tr></thead>
<tbody>
<tr><td>10</td><td>"Describe a shift where something went wrong — a delay, a shortage or a safety issue. What did you do, and who did you tell?"</td><td>Escalation rather than improvising; awareness of rules</td></tr>
<tr><td>11</td><td>"You have three urgent tasks and time for two before the end of the day. How do you decide, and what do you tell the person whose task waits?"</td><td>A decision rule and proactive communication</td></tr>
<tr><td>12</td><td>"Tell us about a process you improved. How did you know it worked?"</td><td>A before-and-after measure</td></tr>
</tbody>
</table>

<h3>Technical and specialist roles</h3>
<table>
<thead><tr><th>#</th><th>Question</th><th>A strong answer contains</th></tr></thead>
<tbody>
<tr><td>13</td><td>"Explain a technical problem you solved recently to someone outside your field: the problem, your approach and how you knew it was fixed."</td><td>Clarity for a non-expert and a verification step</td></tr>
<tr><td>14</td><td>"Tell us about a mistake in your work that reached someone else. What happened next?"</td><td>Ownership and a change that prevents repeats</td></tr>
<tr><td>15</td><td>"Which tool or method in this job description do you know best? Describe the last time you used it."</td><td>Recent, specific use</td></tr>
</tbody>
</table>

<h3>Team leads and managers</h3>
<table>
<thead><tr><th>#</th><th>Question</th><th>A strong answer contains</th></tr></thead>
<tbody>
<tr><td>16</td><td>"Describe a time you had to give difficult feedback to someone in your team. How did you do it and what changed?"</td><td>A direct conversation and a follow-up</td></tr>
<tr><td>17</td><td>"Tell us about a decision you made that your team disagreed with."</td><td>Reasoning, listening, and how commitment was built</td></tr>
<tr><td>18</td><td>"How did you handle a period when your team was short-staffed?"</td><td>Prioritisation and protecting people and quality</td></tr>
</tbody>
</table>

<h3>Any role: judgement and learning</h3>
<table>
<thead><tr><th>#</th><th>Question</th><th>A strong answer contains</th></tr></thead>
<tbody>
<tr><td>19</td><td>"Tell us about a task you were given with incomplete instructions. What did you assume, what did you check, and what was the result?"</td><td>Checking before assuming; a named person they asked</td></tr>
<tr><td>20</td><td>"Describe feedback that changed how you work."</td><td>Specific feedback and a behavior change</td></tr>
<tr><td>21</td><td>"Tell us about something you had to learn quickly for work. How did you do it?"</td><td>A method and a result</td></tr>
<tr><td>22</td><td>"Describe a time you disagreed with a manager or client. What did you do?"</td><td>A respectful challenge and an outcome</td></tr>
</tbody>
</table>

<h3>Practical fit (keep these last)</h3>
<table>
<thead><tr><th>#</th><th>Question</th><th>A strong answer contains</th></tr></thead>
<tbody>
<tr><td>23</td><td>"Please confirm your notice period and earliest start date."</td><td>Clear dates</td></tr>
<tr><td>24</td><td>"How would you travel to the work location, and are the hours workable for you?"</td><td>A realistic plan</td></tr>
<tr><td>25</td><td>"Is there anything you would like us to know to assess you fairly for this role?"</td><td>An opportunity to raise adjustments or context</td></tr>
</tbody>
</table>
<p>Pick five or six: one warm-up, three or four behavioral questions for the competencies that decide the role, and one practical question. For live structured interviews, use the larger bank of <a href="/blog/structured-interview-questions">40 structured interview questions</a>.</p>

<h2>Video interview question checklist</h2>
<ul>
<li>Can it be answered well in 60–90 seconds?</li>
<li>Is it about one situation?</li>
<li>Would every candidate read it the same way? Read it aloud once.</li>
<li>Does it ask for the candidate's own action and an outcome?</li>
<li>Is it clearly job-related — could you explain to a candidate why you asked it?</li>
<li>Does it avoid age, family, health, religion, nationality and other protected characteristics?</li>
<li>Does it avoid trick questions that mainly measure confidence?</li>
<li>Have you written down what a strong answer contains before reviewing the first one?</li>
</ul>

<h2>How to score recorded answers</h2>
<p>Write the evidence you expect for each question (the right-hand columns above) and rate each answer on a short anchored scale. Compare candidates question by question. The <a href="/blog/interview-scorecard-template">interview scorecard template</a> has a 1–5 scale you can reuse. Tell candidates what is recorded, who sees it and that a person reviews answers before any decision.</p>

<h2>Common video interview questions — for candidates</h2>
<p>If you are preparing for a recorded interview, expect a motivation question, three or four "tell me about a time" questions tied to the job description, and a practical question. Answer the exact question, give one specific example with what you did and the result, and finish before the timer. More tips are in <a href="/blog/what-is-an-ai-interview">what is an AI interview</a>.</p>

<h2>Using these questions in Beatview</h2>
<p>In Beatview you add your questions to the job's interview with a time limit for each; question sets can be recorded video or multiple choice. Every invited candidate answers the same set, and each answer is scored on relevance, clarity, depth of knowledge and overall, with written feedback. There are no adaptive follow-ups — write the follow-up into the question — and no custom weighting; the overall match score is the plain average of the resume and interview scores. See <a href="/ai-interviews">AI interviews</a>.</p>
`;
